import { MONITOR_NO_ACCESS_EVENTS } from './event';

const staffRoles = {
  proctor: 'Proctor',
  hallMonitor: 'Hall Monitor',
  roomMonitor: 'Room Monitor',
  technologyCoordinator: 'Technology Monitor',
  monitorNoAccess: 'Monitor—No Access',
} as const;

const cbAdminRoleProps = {
  CB_ADMIN_USER_RID: '803',
  CB_ADMIN_USER_ROLE: 'CBAdmin',
};

const roles = {
  admin: 'Coordinator',
  ...staffRoles,
} as const;

const ROLES_MAP = {
  ADMIN: 'admin',
  PROCTOR: 'proctor',
  HALL_MONITOR: 'hallMonitor',
  ROOM_MONITOR: 'roomMonitor',
  TECHNOLOGY_COORDINATOR: 'technologyCoordinator',
  MONITOR_NO_ACCESS: 'monitorNoAccess',
};

type TypeRolesMap = (typeof ROLES_MAP)[keyof typeof ROLES_MAP];

enum ROLES_ENUM {
  ADMIN = 'admin',
  PROCTOR = 'proctor',
  HALL_MONITOR = 'hallMonitor',
  ROOM_MONITOR = 'roomMonitor',
  TECHNOLOGY_COORDINATOR = 'technologyCoordinator',
  MONITOR_NO_ACCESS = 'monitorNoAccess',
}

const digitalRoles = {
  admin: 'Coordinator',
  ...staffRoles,
};

type roleDisplayType = { title: string; value: TypeRolesMap; requireAdmin?: boolean };

const rolesDisplay: roleDisplayType[] = [
  {
    title: roles.admin,
    value: ROLES_MAP.ADMIN,
    requireAdmin: true,
  },
  { title: staffRoles.proctor, value: ROLES_MAP.PROCTOR },
  { title: staffRoles.hallMonitor, value: ROLES_MAP.HALL_MONITOR },
  { title: staffRoles.roomMonitor, value: ROLES_MAP.ROOM_MONITOR },
];

const digitalrolesDisplay: roleDisplayType[] = [
  ...rolesDisplay,
  { title: staffRoles.technologyCoordinator, value: ROLES_MAP.TECHNOLOGY_COORDINATOR },
];

const monitorNoAccessDisplayItem = { title: staffRoles.monitorNoAccess, value: ROLES_MAP.MONITOR_NO_ACCESS };

function isMonitorNoAccessSupported(tdtkAsmtType: string): boolean {
  return (MONITOR_NO_ACCESS_EVENTS as string[]).includes(tdtkAsmtType);
}

// eslint-disable-next-line
function getRoleList(orgEvent: Record<string, any>): roleDisplayType[] {
  if (!orgEvent) {
    return [];
  }
  const display: roleDisplayType[] = digitalrolesDisplay;

  const result = [...display];

  if (isMonitorNoAccessSupported(orgEvent.tdtkAsmtType)) {
    result.push(monitorNoAccessDisplayItem);
  }
  return result;
}
/**
 * Remove staff that are non PII roles.
 * @param staff staff array
 * @returns staff array
 */
export function filterStaffByPIIRole(staff: { role: string }[]): { role: string }[] {
  return staff.filter((s) => {
    return s.role !== ROLES_MAP.TECHNOLOGY_COORDINATOR && s.role !== ROLES_MAP.MONITOR_NO_ACCESS;
  });
}

const allRoles = Object.keys(roles);

// @todo Remove CBAdmin when it's not needed.
allRoles.push('CBAdmin');

const getTitle = (roleName: string) => {
  const matchingRole = digitalrolesDisplay.find((item) => {
    return item.value === roleName;
  });

  return matchingRole ? matchingRole.title : '';
};

const rolesWithNoRoomAssignments = [
  ROLES_MAP.TECHNOLOGY_COORDINATOR,
  ROLES_MAP.HALL_MONITOR,
  ROLES_MAP.MONITOR_NO_ACCESS,
] as const;

function isRoomDisabledForRole(role: string): boolean {
  return rolesWithNoRoomAssignments.includes(role);
}

const rolesWithNoToolkitAccess = [ROLES_MAP.MONITOR_NO_ACCESS] as const;

function isToolkitAccessDisabledForRole(role: string): boolean {
  return rolesWithNoToolkitAccess.includes(role);
}

export {
  roles,
  allRoles,
  getRoleList,
  getTitle,
  staffRoles,
  rolesDisplay,
  digitalrolesDisplay,
  digitalRoles,
  cbAdminRoleProps,
  ROLES_MAP,
  ROLES_ENUM,
  isMonitorNoAccessSupported,
  isRoomDisabledForRole,
  isToolkitAccessDisabledForRole,
};
